import { Controller } from "@hotwired/stimulus"
import ToastController from "./toast_controller"
import { refreshParticipantLists } from "../lib/refresh_participant_lists"

export default class extends Controller {
  static targets = [
    "emailSection", "input", "tagsContainer", "copyLinkButton", "completeButton", "sendButton",
    "toggleContainer", "personaSelect",
    "existingParticipantsSection", "existingParticipantsLoading", "existingParticipantsList", "existingParticipantsEmpty"
  ]
  static values = {
    activityId: String,
    inloopId: String,
    participantsUrl: String
  }

  connect() {
    this.emails = []

    // Listen to toggle changes
    if (this.hasToggleContainerTarget) {
      const checkbox = this.toggleContainerTarget.querySelector('input[type="checkbox"]')
      if (checkbox) {
        checkbox.addEventListener('change', (e) => this.toggle(e))
      }
    }
  }

  toggle(event) {
    const isChecked = event.target.checked

    if (isChecked) {
      // Show email input section
      this.emailSectionTarget.classList.remove("hidden")
      this.emailSectionTarget.classList.add("flex", "flex-col", "gap-1.5")

      // Hide "Copy Link" button and "Complete" button
      if (this.hasCopyLinkButtonTarget) {
        this.copyLinkButtonTarget.classList.add("hidden")
      }
      if (this.hasCompleteButtonTarget) {
        this.completeButtonTarget.classList.add("hidden")
      }

      // Show "Send Invitations" button
      if (this.hasSendButtonTarget) {
        this.sendButtonTarget.classList.remove("hidden")
      }

      // Focus input and load existing participants
      if (this.hasInputTarget) {
        this.inputTarget.focus()
      }
      this.fetchExistingParticipants()
    } else {
      // Hide email input section
      this.emailSectionTarget.classList.add("hidden")
      this.emailSectionTarget.classList.remove("flex", "flex-col", "gap-1.5")

      // Show "Copy Link" button and "Complete" button
      if (this.hasCopyLinkButtonTarget) {
        this.copyLinkButtonTarget.classList.remove("hidden")
      }
      if (this.hasCompleteButtonTarget) {
        this.completeButtonTarget.classList.remove("hidden")
      }

      // Hide "Send Invitations" button
      if (this.hasSendButtonTarget) {
        this.sendButtonTarget.classList.add("hidden")
      }

      // Clear emails
      this.emails = []
      this.renderTags()
    }
  }

  async fetchExistingParticipants() {
    if (!this.hasParticipantsUrlValue || !this.hasExistingParticipantsListTarget) return

    // Show loading, hide list + empty state
    this.existingParticipantsLoadingTarget.classList.remove("hidden")
    this.existingParticipantsListTarget.classList.add("hidden")
    this.existingParticipantsListTarget.classList.remove("flex")
    this.existingParticipantsEmptyTarget.classList.add("hidden")

    try {
      const response = await fetch(this.participantsUrlValue, {
        headers: { "Accept": "application/json", "X-CSRF-Token": this.csrfToken }
      })
      const data = await response.json()
      const participants = data.participants || []

      this.existingParticipantsLoadingTarget.classList.add("hidden")

      if (participants.length === 0) {
        this.existingParticipantsEmptyTarget.classList.remove("hidden")
        return
      }

      this.renderExistingParticipants(participants)
      this.existingParticipantsListTarget.classList.remove("hidden")
      this.existingParticipantsListTarget.classList.add("flex")
    } catch (e) {
      this.existingParticipantsLoadingTarget.classList.add("hidden")
      this.existingParticipantsEmptyTarget.classList.remove("hidden")
    }
  }

  renderExistingParticipants(participants) {
    const list = this.existingParticipantsListTarget
    list.innerHTML = participants.map(p => {
      const alreadyAdded = this.emails.includes(p.email)
      return `
        <button type="button"
                class="existing-participant-chip inline-flex items-center gap-1.5 px-2 py-1 rounded-full border text-xs font-medium transition-colors
                       ${alreadyAdded
                         ? 'bg-[#4427BF] border-[#4427BF] text-white cursor-default'
                         : 'bg-white border-[#E4E5E8] text-[#374151] hover:border-[#4427BF] hover:text-[#4427BF] cursor-pointer'}"
                data-email="${p.email}"
                data-name="${p.name}"
                ${alreadyAdded ? 'disabled' : ''}
                data-action="click->email-invitation#selectParticipant">
          <span class="w-4 h-4 rounded-full bg-[#4427BF] flex items-center justify-center text-white text-[8px] font-bold flex-shrink-0 ${alreadyAdded ? 'bg-white/30' : ''}">
            ${p.avatar}
          </span>
          <span class="max-w-[120px] truncate">${p.name || p.email}</span>
          ${alreadyAdded
            ? '<svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="3" stroke-linecap="round" stroke-linejoin="round"><path d="M20 6L9 17l-5-5"/></svg>'
            : '<svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"><path d="M12 5v14M5 12h14"/></svg>'
          }
        </button>
      `
    }).join("")
  }

  selectParticipant(event) {
    const btn = event.currentTarget
    const email = btn.dataset.email
    if (!email || this.emails.includes(email)) return

    this.addEmail(email)
    this.syncParticipantChips()
  }

  handleKeydown(event) {
    const input = event.target
    const value = input.value.trim()

    // Handle Enter or Comma
    if (event.key === "Enter" || event.key === ",") {
      event.preventDefault()
      if (value) {
        this.addEmail(value)
        input.value = ""
      }
    }

    // Handle Backspace on empty input - remove last tag
    if (event.key === "Backspace" && !value && this.emails.length > 0) {
      this.emails.pop()
      this.renderTags()
      this.syncParticipantChips()
    }
  }

  handleBlur(event) {
    const value = event.target.value.trim()
    if (value) {
      this.addEmail(value)
      event.target.value = ""
    }
  }

  addEmail(email) {
    // Basic email validation
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
    if (emailRegex.test(email) && !this.emails.includes(email)) {
      this.emails.push(email)
      this.renderTags()
    }
  }

  removeEmail(event) {
    const tag = event.currentTarget.closest("[data-tag-value]")
    const email = tag?.dataset.tagValue
    if (!email) return
    this.emails = this.emails.filter(e => e !== email)
    this.renderTags()
    this.syncParticipantChips()
  }

  renderTags() {
    // Match guideline tab personas styling
    const tagsHTML = this.emails.map(email => `
      <span class="inline-flex items-center gap-1 px-2 py-1 bg-[#F2F3F5] rounded-full text-xs font-medium text-[#374151]"
            data-tag-value="${email}">
        ${email}
        <button type="button" class="hover:text-[#B42318]" data-action="click->email-invitation#removeEmail">
          <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
            <path d="M18 6L6 18M6 6L18 18"/>
          </svg>
        </button>
      </span>
    `).join('')

    // Find tags container
    const container = this.tagsContainerTarget

    // Clear existing tags (remove all children)
    container.innerHTML = ''

    // Insert tags
    if (tagsHTML) {
      container.innerHTML = tagsHTML
    }
  }

  syncParticipantChips() {
    if (!this.hasExistingParticipantsListTarget) return
    this.existingParticipantsListTarget.querySelectorAll(".existing-participant-chip").forEach(btn => {
      const email = btn.dataset.email
      const selected = this.emails.includes(email)
      if (selected) {
        btn.className = "existing-participant-chip inline-flex items-center gap-1.5 px-2 py-1 rounded-full border text-xs font-medium transition-colors bg-[#4427BF] border-[#4427BF] text-white cursor-default"
        btn.disabled = true
      } else {
        btn.className = "existing-participant-chip inline-flex items-center gap-1.5 px-2 py-1 rounded-full border text-xs font-medium transition-colors bg-white border-[#E4E5E8] text-[#374151] hover:border-[#4427BF] hover:text-[#4427BF] cursor-pointer"
        btn.disabled = false
      }
    })
  }

  focusInput() {
    this.inputTarget.focus()
  }

  async sendInvitations() {
    if (this.emails.length === 0) {
      this.showToast("Please enter at least one email address", "error")
      return
    }

    if (!this.hasInloopIdValue || !this.activityIdValue) {
      this.showToast("Missing activity or inloop ID", "error")
      return
    }

    // Show loading state
    const sendButton = this.sendButtonTarget.querySelector('button')
    const originalText = sendButton ? sendButton.textContent : 'Send Invitations'
    if (sendButton) {
      sendButton.disabled = true
      sendButton.textContent = 'Sending...'
    }

    try {

      // Call the participants create endpoint to create invites and send emails
      const url = `/facilitator/inloops/${this.inloopIdValue}/activities/${this.activityIdValue}/participants`
      const response = await fetch(url, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-CSRF-Token': this.csrfToken,
          'Accept': 'application/json'
        },
        body: JSON.stringify({
          emails: this.emails.join(', '),
          persona: this.hasPersonaSelectTarget ? this.personaSelectTarget.value : null
        })
      })

      const data = await response.json()

      const count = data.invited?.length || 0
      if (response.ok && count > 0) {
        this.showToast(`Invitations sent to ${count} recipient(s)!`, "success")

        // Clear emails and reset UI
        this.emails = []
        this.renderTags()

        this.closeModal()
        try {
          await refreshParticipantLists(this.activityIdValue, this.inloopIdValue)
        } catch (error) {
          console.error('Error refreshing participants:', error)
          this.showToast("Invitations were sent, but the participant list could not refresh. Please refresh the page.", "error")
        }
        if (data.errors?.length) this.showToast(data.errors.join(" "), "error")
      } else {
        this.showToast(data.errors?.join(" ") || data.error || "Failed to send invitations", "error")
      }
    } catch (error) {
      console.error('Error sending invitations:', error)
      this.showToast("Network error. Please try again.", "error")
    } finally {
      // Restore button state
      if (sendButton) {
        sendButton.disabled = false
        sendButton.textContent = originalText
      }
    }
  }

  closeModal() {
    // Find and close the modal
    const modalElement = this.element.querySelector('[data-controller="modal"]')
    if (modalElement) {
      const modalController = this.application.getControllerForElementAndIdentifier(
        modalElement,
        "modal"
      )
      if (modalController) {
        modalController.close()
      }
    }
  }

  showToast(message, type = "success") {
    // Use the toast controller for consistent notifications
    const toast = new ToastController()
    toast.show(message, type)
  }

  get csrfToken() {
    return document.querySelector('meta[name="csrf-token"]')?.content || ""
  }
}
